import React, { useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight, ChevronDown, Shield, Leaf, Compass, Sparkles } from 'lucide-react';
import { menuItems, siteConfig, treatmentCategories } from '../../data/site-content';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const iconMap = {
  Shield: Shield,
  Leaf: Leaf,
  Compass: Compass,
  Sparkles: Sparkles
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeSubDropdown, setActiveSubDropdown] = useState(null);
  const [activeMobileDropdown, setActiveMobileDropdown] = useState(null);
  const [activeMobileSubDropdown, setActiveMobileSubDropdown] = useState(null);
  const location = useLocation();

  const normalizePath = (p) => (p && p.endsWith('/') && p.length > 1) ? p.slice(0, -1) : p;

  const isSubItemActive = (dropdown) => {
    return dropdown.some(item => {
      if (normalizePath(location.pathname) === normalizePath(item.path)) return true;
      if (item.dropdown) return isSubItemActive(item.dropdown);
      return false;
    });
  };

  return (
    <nav className="bg-white border-b border-[#E8DDCE] sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20 lg:h-28">
          
          {/* Logo Branding */}
          <Link to="/" className="flex items-center group transition-transform duration-500 hover:scale-[1.02]">
            <img 
              src="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/8d119a2b-621a-4bb4-bc1e-9933e5786d8e.png" 
              alt="Corehypnose - Lars Nissen Corell – Psykoterapeut MPF" 
              className="h-12 lg:h-16 w-auto object-contain"
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8">
            {menuItems.map((item) => (
              item.dropdown ? (
                <div 
                  key={item.label}
                  className="relative group h-full flex items-center"
                  onMouseEnter={() => setActiveDropdown(item.label)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <NavLink
                    to={item.path}
                    className={({ isActive }) => cn(
                      "text-[12px] font-medium transition-all uppercase tracking-[0.2em] relative py-1 flex items-center gap-1 cursor-pointer",
                      "after:absolute after:bottom-0 after:left-0 after:w-full after:h-[1px] after:bg-[#123F13] after:scale-x-0 after:transition-transform duration-300",
                      (isActive || isSubItemActive(item.dropdown) || activeDropdown === item.label)
                        ? "text-[#123F13] after:scale-x-100" 
                        : "text-[#3B352F]/70 hover:text-[#123F13] hover:after:scale-x-100"
                    )}
                  >
                    {item.label}
                    <ChevronDown size={12} className={cn("transition-transform duration-300", activeDropdown === item.label && "rotate-180")} />
                  </NavLink>

                  {/* Dropdown Menu */}
                  {item.label === "Behandlinger" ? (
                    <div 
                      className={cn(
                        "absolute top-full left-1/2 -translate-x-1/2 w-[920px] bg-white border border-[#E8DDCE] shadow-xl rounded-xl p-8 z-50 transition-all duration-300",
                        activeDropdown === item.label ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2"
                      )}
                    >
                      <div className="grid grid-cols-4 gap-6">
                        {treatmentCategories.map((cat) => {
                          const IconComponent = iconMap[cat.icon];
                          return (
                            <div key={cat.title} className="flex flex-col">
                              <div className="flex items-center gap-2 text-[#123F13] font-serif font-semibold text-sm tracking-wide border-b border-[#E8DDCE]/40 pb-2 mb-3">
                                {IconComponent && <IconComponent size={16} className="text-[#8C2F12]" />}
                                <span>{cat.title}</span>
                              </div>
                              <p className="text-[11px] text-[#3B352F]/60 mb-4 leading-relaxed h-10">
                                {cat.description}
                              </p>
                              <div className="flex flex-col gap-1.5">
                                {cat.items.map((subItem) => (
                                  <NavLink
                                    key={subItem.label}
                                    to={subItem.path}
                                    className={({ isActive }) => cn(
                                      "text-xs transition-all duration-200 text-left py-1 flex items-center gap-1 hover:translate-x-1",
                                      isActive 
                                        ? "text-[#8C2F12] font-semibold" 
                                        : "text-[#3B352F]/70 hover:text-[#123F13]"
                                    )}
                                  >
                                    <ChevronRight size={10} className="opacity-40 shrink-0" />
                                    <span>{subItem.label}</span>
                                  </NavLink>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div 
                      className={cn(
                        "absolute top-full left-1/2 -translate-x-1/2 w-64 bg-white border border-[#E8DDCE] shadow-xl py-4 z-50 transition-all duration-300",
                        activeDropdown === item.label ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2"
                      )}
                    >
                      <div className="flex flex-col">
                        {item.dropdown.map((subItem) => (
                          <div 
                            key={subItem.label}
                            className="relative group/sub"
                            onMouseEnter={() => subItem.dropdown && setActiveSubDropdown(subItem.label)}
                            onMouseLeave={() => subItem.dropdown && setActiveSubDropdown(null)}
                          >
                            <NavLink
                              to={subItem.path}
                              className={({ isActive }) => cn(
                                "px-6 py-2.5 text-[11px] uppercase tracking-widest transition-colors duration-200 text-left flex justify-between items-center",
                                (isActive || (subItem.dropdown && isSubItemActive(subItem.dropdown)))
                                  ? "bg-[#FAF7F2] text-[#123F13] font-semibold" 
                                  : "text-[#3B352F]/70 hover:bg-[#FAF7F2] hover:text-[#123F13]"
                              )}
                            >
                              {subItem.label}
                              {subItem.dropdown && <ChevronRight size={12} className={cn("transition-transform duration-300", activeSubDropdown === subItem.label && "translate-x-1")} />}
                            </NavLink>

                            {/* Nested Dropdown */}
                            {subItem.dropdown && (
                              <div 
                                className={cn(
                                  "absolute left-full top-0 w-64 bg-white border border-[#E8DDCE] shadow-xl py-4 z-[60] transition-all duration-300 ml-[-1px]",
                                  activeSubDropdown === subItem.label ? "opacity-100 visible translate-x-0" : "opacity-0 invisible -translate-x-2"
                                )}
                              >
                                <div className="flex flex-col">
                                  {subItem.dropdown.map((nestedItem) => (
                                    <NavLink
                                      key={nestedItem.label}
                                      to={nestedItem.path}
                                      className={({ isActive }) => cn(
                                        "px-6 py-2.5 text-[11px] uppercase tracking-widest transition-colors duration-200 text-left",
                                        isActive 
                                          ? "bg-[#FAF7F2] text-[#123F13] font-semibold" 
                                          : "text-[#3B352F]/70 hover:bg-[#FAF7F2] hover:text-[#123F13]"
                                      )}
                                    >
                                      {nestedItem.label}
                                    </NavLink>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => cn(
                    "text-[12px] font-medium transition-all uppercase tracking-[0.2em] relative py-1",
                    "after:absolute after:bottom-0 after:left-0 after:w-full after:h-[1px] after:bg-[#123F13] after:scale-x-0 after:transition-transform duration-300",
                    isActive 
                      ? "text-[#123F13] after:scale-x-100" 
                      : "text-[#3B352F]/70 hover:text-[#123F13] hover:after:scale-x-100",
                    item.primary && "bg-[#123F13] text-white px-6 py-2.5 hover:bg-[#123F13]/90 hover:text-white after:hidden rounded-none ml-4"
                  )}
                >
                  {item.label}
                </NavLink>
              )
            ))}
          </div>

          {/* Mobile Controls */}
          <div className="flex items-center gap-2 lg:hidden">
            <a 
              href="tel:+4527204671" 
              className="flex items-center gap-1.5 text-xs font-bold text-[#123F13] bg-[#FAF7F2] border border-[#E8DDCE] px-3 py-2 rounded-full hover:bg-[#E8DDCE]/20 transition-colors"
              aria-label="Ring til Core Hypnose"
            >
              <i className="fa-solid fa-phone text-[#8C2F12]" aria-hidden="true" />
              <span>27 20 46 71</span>
            </a>
            <button 
              className="p-2 text-[#123F13]"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle Menu"
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>


      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div className="lg:hidden fixed inset-0 top-20 bg-white z-40 overflow-y-auto animate-in fade-in slide-in-from-top-4 duration-300">
          <div className="flex flex-col p-6 gap-2">
            {/* Mobile Info Summary */}
            <div className="mb-6 p-4 bg-[#FAF7F2] rounded-lg">
              <p className="text-sm font-semibold text-[#123F13]">{siteConfig.contact.primaryTitle}</p>
              <p className="text-xs text-[#3B352F]/70 mt-1">{siteConfig.contact.titles[1]}</p>
              <div className="mt-4 flex flex-col gap-2">
                <a href={`tel:${siteConfig.contact.phone}`} className="text-sm text-[#123F13] flex items-center gap-2">
                  <ChevronRight size={14} /> {siteConfig.contact.phone}
                </a>
              </div>
            </div>

            {menuItems.map((item) => (
              <div key={item.label}>
                {item.dropdown ? (
                  <div className="flex flex-col border-b border-[#F4EFE7]">
                    <button
                      onClick={() => setActiveMobileDropdown(activeMobileDropdown === item.label ? null : item.label)}
                      className={cn(
                        "py-4 text-base font-medium flex justify-between items-center w-full",
                        (isSubItemActive(item.dropdown) || activeMobileDropdown === item.label) ? "text-[#123F13]" : "text-[#3B352F]"
                      )}
                    >
                      {item.label}
                      <ChevronDown size={18} className={cn("transition-transform duration-300 text-[#E8DDCE]", activeMobileDropdown === item.label && "rotate-180")} />
                    </button>
                    <div className={cn(
                      "flex flex-col bg-[#FAF7F2] overflow-hidden transition-all duration-300",
                      activeMobileDropdown === item.label ? "max-h-[1200px] mb-4" : "max-h-0"
                    )}>
                      {item.label === "Behandlinger" ? (
                        <div className="flex flex-col">
                          <NavLink
                            to={item.path}
                            onClick={() => setIsOpen(false)}
                            className={({ isActive }) => cn(
                              "py-3 px-6 text-sm flex justify-between items-center border-b border-[#E8DDCE]/30 bg-[#F4EFE7]/50 font-semibold text-[#123F13]"
                            )}
                          >
                            Oversigt: Alle behandlinger
                            <ChevronRight size={14} />
                          </NavLink>
                          
                          {treatmentCategories.map((cat) => {
                            const IconComponent = iconMap[cat.icon];
                            return (
                              <div key={cat.title} className="flex flex-col border-b border-[#E8DDCE]/20 last:border-0">
                                <div className="py-3 px-6 bg-[#F4EFE7]/30 flex items-center gap-2 text-[#123F13] font-serif font-semibold text-xs tracking-wide">
                                  {IconComponent && <IconComponent size={14} className="text-[#8C2F12]" />}
                                  <span>{cat.title}</span>
                                </div>
                                <div className="flex flex-col pl-4">
                                  {cat.items.map((subItem) => (
                                    <NavLink
                                      key={subItem.label}
                                      to={subItem.path}
                                      onClick={() => setIsOpen(false)}
                                      className={({ isActive }) => cn(
                                        "py-2.5 px-6 text-xs flex justify-between items-center border-b border-[#E8DDCE]/10 last:border-0",
                                        isActive ? "text-[#8C2F12] font-semibold" : "text-[#3B352F]/70"
                                      )}
                                    >
                                      {subItem.label}
                                      <ChevronRight size={12} className="opacity-30" />
                                    </NavLink>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <>
                          <NavLink
                            to={item.path}
                            onClick={() => setIsOpen(false)}
                            className={({ isActive }) => cn(
                              "py-3 px-6 text-sm flex justify-between items-center border-b border-[#E8DDCE]/30 bg-[#F4EFE7]/50",
                              isActive ? "text-[#123F13] font-semibold" : "text-[#123F13] font-medium"
                            )}
                          >
                            Oversigt: {item.label}
                            <ChevronRight size={14} />
                          </NavLink>
                          {item.dropdown.map((subItem) => (
                            <div key={subItem.label} className="flex flex-col">
                              {subItem.dropdown ? (
                                <>
                                  <div className="flex items-center w-full border-b border-[#E8DDCE]/30">
                                    <NavLink
                                      to={subItem.path}
                                      onClick={() => setIsOpen(false)}
                                      className={({ isActive }) => cn(
                                        "py-3 px-6 text-sm flex-grow font-medium",
                                        isActive ? "text-[#123F13] font-semibold" : "text-[#3B352F]/70"
                                      )}
                                    >
                                      {subItem.label}
                                    </NavLink>
                                    <button
                                      onClick={() => setActiveMobileSubDropdown(activeMobileSubDropdown === subItem.label ? null : subItem.label)}
                                      className="p-3 border-l border-[#E8DDCE]/30 text-[#123F13]"
                                    >
                                      <ChevronDown size={16} className={cn("transition-transform duration-300", activeMobileSubDropdown === subItem.label && "rotate-180")} />
                                    </button>
                                  </div>
                                  <div className={cn(
                                    "flex flex-col bg-[#F4EFE7]/30 overflow-hidden transition-all duration-300",
                                    activeMobileSubDropdown === subItem.label ? "max-h-[400px]" : "max-h-0"
                                  )}>
                                    {subItem.dropdown.map((nestedItem) => (
                                      <NavLink
                                        key={nestedItem.label}
                                        to={nestedItem.path}
                                        onClick={() => setIsOpen(false)}
                                        className={({ isActive }) => cn(
                                          "py-3 px-10 text-sm flex justify-between items-center border-b border-[#E8DDCE]/20 last:border-0",
                                          isActive ? "text-[#123F13] font-semibold" : "text-[#3B352F]/60"
                                        )}
                                      >
                                        {nestedItem.label}
                                        <ChevronRight size={12} className="opacity-20" />
                                      </NavLink>
                                    ))}
                                  </div>
                                </>
                              ) : (
                                <NavLink
                                  to={subItem.path}
                                  onClick={() => setIsOpen(false)}
                                  className={({ isActive }) => cn(
                                    "py-3 px-6 text-sm flex justify-between items-center border-b border-[#E8DDCE]/30 last:border-0",
                                    isActive ? "text-[#123F13] font-semibold" : "text-[#3B352F]/70"
                                  )}
                                >
                                  {subItem.label}
                                  <ChevronRight size={14} className="opacity-30" />
                                </NavLink>
                              )}
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={({ isActive }) => cn(
                      "py-4 border-b border-[#F4EFE7] text-base font-medium flex justify-between items-center",
                      isActive ? "text-[#123F13]" : "text-[#3B352F]",
                      item.primary && "mt-4 bg-[#123F13] text-white px-6 rounded-none border-none py-4 text-center justify-center uppercase tracking-[0.2em] text-sm"
                    )}
                  >
                    {item.label}
                    {!item.primary && <ChevronRight size={18} className="text-[#E8DDCE]" />}
                  </NavLink>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
